import { useEffect, useState } from "react";

const PROBE_ID = "a11y-animation-probe";
const PROBE_STYLE_ID = "a11y-animation-probe-style";
const ROOT_CLASS = "a11y-pause-motion";

function ensureProbe() {
  if (typeof document === "undefined") return null;

  if (!document.getElementById(PROBE_STYLE_ID)) {
    const style = document.createElement("style");
    style.id = PROBE_STYLE_ID;
    style.textContent =
      "@keyframes a11y-animation-probe-kf { from { transform: translate3d(0,0,0); } to { transform: translate3d(1px,0,0); } }";
    document.head.appendChild(style);
  }

  let el = document.getElementById(PROBE_ID);
  if (!el) {
    el = document.createElement("div");
    el.id = PROBE_ID;
    el.setAttribute("aria-hidden", "true");
    el.style.cssText =
      "position:fixed;width:1px;height:1px;left:-9999px;top:0;pointer-events:none;animation:a11y-animation-probe-kf 2s linear infinite";
    document.body.appendChild(el);
  }
  return el;
}

export function isGlobalAnimationPaused() {
  if (typeof window === "undefined" || !window.getComputedStyle) return false;
  const el = ensureProbe();
  if (!el) return false;
  return window.getComputedStyle(el).animationPlayState === "paused";
}

/**
 * True when an accessibility widget (e.g. UserWay "Pause animation") pauses CSS animations globally.
 */
export default function useGlobalAnimationPaused() {
  const [paused, setPaused] = useState(() => isGlobalAnimationPaused());

  useEffect(() => {
    ensureProbe();

    const sync = () => {
      const next = isGlobalAnimationPaused();
      setPaused(next);
      document.documentElement.classList.toggle(ROOT_CLASS, next);
    };

    sync();

    const mo = new MutationObserver(sync);
    mo.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class", "style"],
    });
    if (document.body) {
      mo.observe(document.body, { attributes: true, attributeFilter: ["class", "style"] });
    }

    const headMo = new MutationObserver(sync);
    headMo.observe(document.head, { childList: true, subtree: true });

    const onVis = () => sync();
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onVis);

    const interval = window.setInterval(sync, 1500);

    return () => {
      mo.disconnect();
      headMo.disconnect();
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onVis);
      window.clearInterval(interval);
      document.documentElement.classList.remove(ROOT_CLASS);
    };
  }, []);

  return paused;
}
